import { useState } from "react";
import { Ban, Check, Dumbbell, Search, Shield, ShieldOff, Trash2 } from "lucide-react";

import { Avatar, Badge, getInitials, tintForId, useConfirm } from "@/ui";
import { useOpenUserProfile } from "@/features/users/UserProfileSheetProvider";
import { noAutofill } from "@/lib/noAutofill";
import {
  useAdminUsers,
  useBanUser,
  useDemoteTrainer,
  useDemoteUser,
  useDeleteUser,
  usePromoteTrainer,
  usePromoteUser,
  useUnbanUser,
} from "@/features/queries";
import { useAuth } from "@/contexts/AuthContext";

export function AdminUsersPage() {
  const { user: me } = useAuth();
  const openProfile = useOpenUserProfile();
  const [query, setQuery] = useState("");
  const { data: users = [], isLoading } = useAdminUsers(query);
  const ban = useBanUser();
  const unban = useUnbanUser();
  const del = useDeleteUser();
  const promote = usePromoteUser();
  const demote = useDemoteUser();
  const promoteTrainer = usePromoteTrainer();
  const demoteTrainer = useDemoteTrainer();
  const confirm = useConfirm();

  return (
    <div>
      <header style={{ marginBottom: 16 }}>
        <h1 className="h1" style={{ margin: 0 }}>Пользователи</h1>
        <p className="caption" style={{ color: "var(--fg2)", marginTop: 4 }}>
          Бан выключает вход через Telegram. Удаление доступно, только если у
          юзера нет матчей и регистраций на турниры.
        </p>
      </header>

      <div className="sp-admin-search">
        <Search size={18} color="var(--fg3)" />
        <input
          value={query}
          placeholder="Найти по имени или @юзернейму"
          onChange={(e) => setQuery(e.target.value)}
          {...noAutofill}
          style={{
            flex: 1,
            border: 0,
            outline: 0,
            background: "transparent",
            fontFamily: "var(--font-body)",
            fontSize: 15,
            color: "var(--fg1)",
          }}
        />
      </div>

      {isLoading ? (
        <p className="body">Загрузка…</p>
      ) : users.length === 0 ? (
        <div className="sp-admin-empty">
          <p>Никого не нашли.</p>
        </div>
      ) : (
        <div className="sp-admin-users-list">
          {users.map((u) => {
            const fullName =
              [u.first_name, u.last_name].filter(Boolean).join(" ") ||
              u.username ||
              "Без имени";
            const isSelf = me?.id === u.id;
            const busy =
              (ban.isPending && ban.variables === u.id) ||
              (unban.isPending && unban.variables === u.id) ||
              (del.isPending && del.variables === u.id) ||
              (promote.isPending && promote.variables === u.id) ||
              (demote.isPending && demote.variables === u.id) ||
              (promoteTrainer.isPending && promoteTrainer.variables === u.id) ||
              (demoteTrainer.isPending && demoteTrainer.variables === u.id);

            return (
              <div key={u.id} className="sp-admin-user-row">
                <button
                  type="button"
                  onClick={() => openProfile(u.id)}
                  aria-label={`Профиль ${u.username ?? u.first_name ?? u.id}`}
                  style={{
                    background: "transparent",
                    border: 0,
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  <Avatar
                    initials={getInitials(u.first_name, u.last_name, u.username)}
                    size={44}
                    tint={tintForId(u.id)}
                    src={u.photo_url}
                  />
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: "var(--font-body)",
                      fontSize: 16,
                      fontWeight: 500,
                      color: u.is_registered ? "var(--fg1)" : "var(--fg3)",
                      textDecoration: u.is_registered ? "none" : "line-through",
                    }}
                  >
                    {fullName}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--fg2)" }}>
                    {u.username ? `@${u.username}` : "—"}
                    {" · "}
                    рейтинг {u.rating}
                  </div>
                </div>

                <div className="sp-admin-user-badges">
                  {u.is_admin && <Badge kind="brand">админ</Badge>}
                  {u.is_trainer && <Badge kind="success">тренер</Badge>}
                  {!u.is_registered && <Badge kind="live">бан</Badge>}
                </div>

                <div className="sp-admin-user-actions">
                  {u.is_admin ? (
                    <ActionBtn
                      title="Снять права админа"
                      icon={<ShieldOff size={14} />}
                      disabled={isSelf || busy}
                      onClick={async () => {
                        const ok = await confirm({
                          title: "Снять права админа?",
                          description: `«${fullName}» потеряет доступ к админке.`,
                          confirmLabel: "Снять права",
                          tone: "danger",
                        });
                        if (ok) demote.mutate(u.id);
                      }}
                    />
                  ) : (
                    <ActionBtn
                      title="Сделать админом"
                      icon={<Shield size={14} />}
                      disabled={!u.is_registered || busy}
                      onClick={async () => {
                        const ok = await confirm({
                          title: "Назначить админом?",
                          description: `«${fullName}» получит полный доступ к админке.`,
                          confirmLabel: "Назначить",
                        });
                        if (ok) promote.mutate(u.id);
                      }}
                    />
                  )}
                  {u.is_trainer ? (
                    <ActionBtn
                      title="Снять статус тренера"
                      icon={<Dumbbell size={14} />}
                      disabled={busy}
                      onClick={async () => {
                        const ok = await confirm({
                          title: "Убрать из тренеров?",
                          description: `«${fullName}» больше не сможет создавать тренировки.`,
                          confirmLabel: "Убрать",
                          tone: "danger",
                        });
                        if (ok) demoteTrainer.mutate(u.id);
                      }}
                    />
                  ) : (
                    <ActionBtn
                      title="Сделать тренером"
                      icon={<Dumbbell size={14} />}
                      disabled={!u.is_registered || busy}
                      onClick={async () => {
                        const ok = await confirm({
                          title: "Назначить тренером?",
                          description: `«${fullName}» сможет создавать и вести тренировки.`,
                          confirmLabel: "Назначить",
                        });
                        if (ok) promoteTrainer.mutate(u.id);
                      }}
                    />
                  )}
                  {u.is_registered ? (
                    <ActionBtn
                      title="Забанить"
                      icon={<Ban size={14} />}
                      disabled={isSelf || u.is_admin || busy}
                      onClick={() => ban.mutate(u.id)}
                    />
                  ) : (
                    <ActionBtn
                      title="Разбанить"
                      icon={<Check size={14} />}
                      disabled={busy}
                      onClick={() => unban.mutate(u.id)}
                    />
                  )}
                  <ActionBtn
                    title="Удалить"
                    icon={<Trash2 size={14} />}
                    danger
                    disabled={isSelf || u.is_admin || busy}
                    onClick={async () => {
                      const ok = await confirm({
                        title: "Удалить пользователя?",
                        description: `«${fullName}» будет удалён навсегда. Действие нельзя отменить.`,
                        confirmLabel: "Удалить",
                        tone: "danger",
                      });
                      if (ok) del.mutate(u.id);
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(del.error || promote.error || demote.error || promoteTrainer.error || demoteTrainer.error || ban.error || unban.error) && (
        <div
          style={{
            marginTop: 12,
            background: "rgba(223,63,64,0.1)",
            color: "var(--fg-danger)",
            padding: "8px 12px",
            borderRadius: "var(--r-md)",
            fontSize: 13,
          }}
        >
          {((del.error || promote.error || demote.error || promoteTrainer.error || demoteTrainer.error || ban.error || unban.error) as Error).message}
        </div>
      )}
    </div>
  );
}

function ActionBtn({
  icon,
  title,
  onClick,
  danger,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 32,
        height: 32,
        borderRadius: "var(--r-md)",
        border: 0,
        background: "var(--bg-secondary)",
        color: danger ? "var(--fg-danger)" : "var(--fg1)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {icon}
    </button>
  );
}
